// src/pages/CompletedProjectsPage.jsx (Database Version)
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import ProjectsSection from '../components/ProjectsSection';
import '../styles/ProjectsPage.css';

const CompletedProjectsPage = () => {
  const [completedProjects, setCompletedProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_BASE_URL = 'https://eks-projectmanager.eksconstruction.in/api';
  const BASE_URL = 'https://eks-projectmanager.eksconstruction.in';

  useEffect(() => {
    const fetchCompletedProjects = async () => {
      try {
        setLoading(true);
        console.log('Fetching completed projects...');
        
        const response = await fetch(`${API_BASE_URL}/get_projects.php`);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const result = await response.json();
        console.log('API Response:', result);
        
        if (result.success && result.projects) {
          // Filter only completed projects
          const completed = result.projects.filter(
            project => project.project_type === 'Completed Projects'
          );
          
          // Map the data to match your ProjectsSection component format
          const mappedProjects = completed.map((project, index) => {
            // Parse key features if it's a string
            const features = project.key_features 
              ? project.key_features.split(',').map(f => f.trim()).filter(f => f)
              : ['Modern Architecture', 'Quality Construction', 'Timely Delivery'];
            
            // Get images array and prepare them with full URLs
            const images = Array.isArray(project.images) && project.images.length > 0
              ? project.images.map(img => `${BASE_URL}${img}`)
              : [];
            
            return {
              id: project.id,
              title: project.client_name,
              location: project.location,
              type: 'Residential', // You can add this field to your database if needed
              area: `${project.area} sqft`,
              floors: project.floors || '2 Floors',
              completedDate: new Date(project.completion_date).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long'
              }),
              image: images[0] || '', // First image as main image
              images: images, // All images for slider
              features: features,
              description: project.project_title || 'A beautiful construction project completed with excellence.',
              color: index % 2 === 0 ? 'blue' : 'red', // Alternate colors
              progress: project.progress,
              phase: project.phase,
              startDate: project.start_date,
              completionDate: project.completion_date
            };
          });
          
          console.log('Mapped completed projects:', mappedProjects);
          setCompletedProjects(mappedProjects);
          setError(null);
        } else {
          throw new Error(result.error || 'Failed to fetch projects');
        }
      } catch (err) {
        console.error('Error fetching completed projects:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCompletedProjects();
  }, [API_BASE_URL, BASE_URL]);

  if (loading) {
    return (
      <div className="loading-container" style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        gap: '20px'
      }}>
        <div className="loading-spinner" style={{
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #3498db',
          borderRadius: '50%',
          width: '50px',
          height: '50px',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ fontSize: '18px', color: '#666' }}>Loading completed projects...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container" style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        gap: '20px',
        padding: '20px'
      }}>
        <div style={{
          fontSize: '48px',
          color: '#e74c3c'
        }}>⚠</div>
        <h2 style={{ color: '#e74c3c', margin: '0' }}>Error Loading Projects</h2>
        <p style={{ color: '#666', textAlign: 'center' }}>{error}</p>
        <button 
          onClick={() => window.location.reload()}
          style={{
            padding: '10px 20px',
            backgroundColor: '#3498db',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '16px'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (completedProjects.length === 0) {
    return (
      <div className="no-projects-container" style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        gap: '20px'
      }}>
        <div style={{ fontSize: '64px' }}>📋</div>
        <h2 style={{ color: '#666', margin: '0' }}>No Completed Projects</h2>
        <p style={{ color: '#999' }}>There are no completed projects to display at the moment.</p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="projects-page"
    >
      <ProjectsSection projects={completedProjects} type="completed" />
    </motion.div>
  );
};

export default CompletedProjectsPage;